<template>
  <v-expansion-panels v-model="expand" flat outlined>
    <v-expansion-panel>
      <v-expansion-panel-header class="px-3">
        <v-card flat>
          <v-card-title class="pa-0 ">
            <v-icon class="me-2" color="orange">mdi-information</v-icon>
            {{ scheduleTitle }} | {{ studyYear }} | {{ $t("invigilatorAssignmentsSummary.round") }}
            {{ roundName }}
          </v-card-title>
        </v-card>
      </v-expansion-panel-header>

      <v-expansion-panel-content>
        <v-row>
          <v-col cols="12" lg="8" md="6" sm="12">
            <div class="bulk-report-wrapper" :class="{ 'dark-mode': $vuetify.theme.dark }">
              <div class="table-responsive">
                <table class="simple-print-table">
                  <thead>
                    <tr>
                      <th>{{ $t("seat.dayAndDate") }}</th>
                      <th>{{ $t("seat.subject") }}</th>
                      <th>{{ $t("seat.time") }}</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr
                      v-for="(item, index) in studentExamSchedule"
                      :key="index"
                      :class="getRowClass(item.dayType)"
                    >
                      <td class="date-cell">
                        <div class="day-name">{{ getDayName(item.examDate) }}</div>
                        <div class="date-sub">{{ item.examDate | moment("YYYY/MM/DD") }}</div>
                      </td>

                      <td class="subject-cell" :colspan="item.dayType === 1 ? 1 : 2">
                        <div v-if="item.dayType === 1" class="text-bold-manager">
                          {{ item.subjectName }}
                        </div>

                        <div v-else class="special-day-text text-reserve-orange">
                          {{
                            item.dayType === 2
                              ? $t("seat.officialHoliday")
                              : $t("seat.weekendHoliday")
                          }}

                          <span v-if="item.dayType === 2"> ({{ item.subjectName }}) </span>
                        </div>
                      </td>

                      <td v-if="item.dayType === 1 && item.startTime" class="time-cell">
                        {{ formatTime(item.startTime) }} -
                        {{ formatTime(item.endTime) }}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </v-col>
          <v-col cols="12" lg="4" md="6" sm="12"  >
            <student-seat :seat-data="seatInfo" ></student-seat>
          </v-col>
        </v-row>
      </v-expansion-panel-content>
    </v-expansion-panel>
  </v-expansion-panels>
</template>
<script>
import StudentSeat from "./student-seat.vue";

export default {
  components: {
    StudentSeat
  },
  props: ["studentExamSchedule", "seatInfo"],
  data() {
    return {
      expand: 0
    };
  },
  computed: {
    scheduleTitle() {
      return this.studentExamSchedule[0].examScheduleName;
    },
    studyYear() {
      return this.studentExamSchedule[0].studyYear;
    },
    roundName() {
      const rounds = {
        1: this.$t("seat.firstRound"),
        2: this.$t("seat.secondRound"),
        3: this.$t("seat.thirdRound"),
        4: this.$t("seat.fourthRound")
      };

      return rounds[this.studentExamSchedule[0].round];
    },
    isDark() {
      return this.$vuetify.theme.dark;
    },
    lang() {
      return this.$i18n.locale;
    }
  },
  methods: {
    formatDate(dateStr) {
      const date = new Date(dateStr);
      return date.toLocaleDateString(this.lang == "ar" ? "ar" : "en");
    },
    getDayName(dateStr) {
      const date = new Date(dateStr);
      return date.toLocaleDateString(this.lang == "ar" ? "ar" : "en", { weekday: "long" });
    },
    formatTime(timeStr) {
      if (!timeStr) return "";
      const date = new Date(timeStr);
      return date.toLocaleTimeString(this.lang == "ar" ? "ar" : "en", {
        hour: "2-digit",
        minute: "2-digit"
      });
    },
    getRowClass(dayType) {
      if (dayType === 2) return "holiday-row";
      if (dayType === 3) return "weekend-row";
      return "exam-row";
    },
    getExamTypeName(type) {
      const types = {
        1: this.$t("seat.assessment"),
        2: this.$t("seat.joint"),
        3: this.$t("seat.nonAssessment")
      };

      return types[type] || "-";
    },
    getExamTypeClass(type) {
      if (type === 1) return "bg-assessment";
      if (type === 2) return "bg-joint ";
      return "";
    },
    getExamTypeText(type) {
      if (type === 1) return "Assessment";
      if (type === 2) return "Joint";
      return "";
    }
  }
};
</script>
<style scoped>
/* Base Theme Variables (Light Mode Defaults) */
.bulk-report-wrapper {
  --app-bg: #ffffff;
  --header-text: #000000;
  --table-border: #e0e0e0;
  --label-bg: #f5f5f5;
  --val-bg: #ffffff;
  --text-muted: #6b7280;
  --row-alt: #fafafa;
  --manager-color: #0c65af; /* primary2 from your theme */
  --reserve-color: #e65100;

  width: 100%;
  padding: 5px;
  background-color: var(--app-bg);
  transition: all 0.3s ease;
  max-width: 1000px;
  margin: auto;
}

/* Dark Mode Integration using your theme colors */
.bulk-report-wrapper.dark-mode {
  --app-bg: #23272f; /* highlight */
  --header-text: #f3f4f6; /* grey-1 */
  --table-border: #4b5563; /* grey-4 */
  --label-bg: #2d323b; /* highlight-1 */
  --val-bg: #23272f; /* highlight */
  --text-muted: #9ca3af; /* grey-4 */
  --row-alt: #2d323b; /* highlight-1 */
  --manager-color: #109afd; /* primary */
  --reserve-color: #ec407a; /* secondary */
}

/* Table Styles */
.simple-print-table {
  width: 100%;
  border-collapse: collapse;
  border: 1px solid var(--table-border);
}

.simple-print-table th,
.simple-print-table td {
  border: 1px solid var(--table-border);
  padding: 5px 5px;
  font-size: 14px;
  color: var(--header-text);
  vertical-align: middle;
  text-align: center;
}

/* Header/Label Styling */
.simple-print-table th {
  font-weight: bold;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

/* Specific Cells */
.date-cell {
  background-color: var(--val-bg) !important;
}

.day-name {
  font-weight: 800;
  font-size: 0.95rem;
}

.date-sub {
  font-size: 0.8rem;
  color: var(--text-muted);
  font-weight: 600;
}

.time-cell {
  font-weight: bold;
  color: var(--header-text);
}

/* Status & Type Colors */
.text-bold-manager {
  font-weight: bold;
  color: var(--manager-color);
}

.text-reserve-orange {
  font-weight: bold;
  color: var(--reserve-color);
}

/* Fixed Requirement Colors (Keep visibility on dark/light) */
.bg-assessment {
  background-color: #93c47d !important;
  color: #000 !important;
  font-weight: bold;
}

.bg-joint {
  background-color: #a2c4c9 !important;
  color: #000 !important;
  font-weight: bold;
}

.table-responsive {
  overflow-x: auto;
  border-radius: 4px;
}
.weekend-row {
  background-color: #d4d3d3 !important;
}
.dark-mode .weekend-row {
  background-color: #1a1a1a !important;
}
.holiday-row {
  background-color: #fffd89 !important;
}
.dark-mode .holiday-row {
  background-color: #554c00 !important;
}

/* Smooth Transition for Panel */
.transparent-panel {
  background-color: transparent !important;
}
</style>
